import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import { types as sdkTypes } from '../util/sdkLoader';
import Decimal from 'decimal.js';
import { timestampToDate } from './dates';
import { ACCESS_OPTIONS, DEFAULT_CURRENCY, DELIVERY_FEE, EMPTY_REVIEW, REQUIRED_UPFRONT, SHIPPING_FEE } from './constants';
import { signupCountries } from '../config/configDefault';
import { isSupportedCurrency } from './currency';
import { supportedCurrencies } from '../config/configStripe';
import moment from 'moment';
import { getLocationWithMapBox } from './api';
import { LISTING_STATE_DRAFT } from './types';
const { LatLng, LatLngBounds, UUID, Money } = sdkTypes;

// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error(
      'Cannot merge resource objects with different ids or types'
    );
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes
    ? { attributes: { ...attributesOld, ...attributesNew } }
    : null;
  const relationships = combinedRelationships(
    oldRes.relationships,
    newRes.relationships
  );
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (
  oldEntities,
  apiResponse,
  sanitizeConfig = {}
) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity
      ? combinedResourceObjects({ ...entity }, current)
      : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (
  entities,
  resources,
  throwIfNotFound = true
) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(
          `Entity with type "${type}" and id "${id ? id.uuid : id}" not found`
        );
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' &&
        value?._ref?.type === 'imageAsset' &&
        value?._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef =
        typeof value == 'object' && value?._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc.id === value._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (
  transaction,
  booking = null,
  listing = null,
  provider = null
) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = {
    id: null,
    type: 'currentUser',
    attributes: { profile: {} },
    profileImage: {},
  };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasDisplayName = user?.attributes?.profile?.displayName;

  if (hasDisplayName) {
    return user.attributes.profile.displayName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (
  objValue,
  srcValue,
  key,
  object,
  source,
  stack
) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

/**
 * A utility function to store data into session storage
 * @param {*} data
 * @param {*} storageKey
 */

export const storeDataToSession = (data, storageKey) => {
  if (window && window.sessionStorage && data) {
    const replacer = function (k, v) {
      if (this[k] instanceof Date) {
        return { date: v, _serializedType: 'SerializableDate' };
      }
      if (this[k] instanceof Decimal) {
        return { decimal: v, _serializedType: 'SerializableDecimal' };
      }
      return sdkTypes.replacer(k, v);
    };

    const storableData = JSON.stringify(
      {
        data,
        storedAt: new Date(),
      },
      replacer
    );
    window.sessionStorage.setItem(storageKey, storableData);
  }
};

export const getSessionStorageData = key => {
  if (window && window.sessionStorage && key) {
    const data = window.sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }
};

export const clearSessionItem = key => {
  if (window && window.sessionStorage && key) {
    window.sessionStorage.clear(key);
  }
};

export function setRentalDateLocalStorage(data) {
  const { dates, package: selectedPackage } = data;
  const {
    bookingStartDate,
    bookingEndDate,
    bookingStartTime,
    bookingEndTime,
  } = dates;

  const startDateMaybe =
    bookingStartDate instanceof Date ? { bookingStartDate } : {};
  const endDateMaybe = bookingEndDate instanceof Date ? { bookingEndDate } : {};
  const startTimeMaybe =
    bookingStartTime && timestampToDate(bookingStartTime) instanceof Date
      ? { bookingStartTime }
      : {};
  const endTimeMaybe =
    bookingEndTime && timestampToDate(bookingEndTime) instanceof Date
      ? { bookingEndTime }
      : {};

  const storableDates = {
    ...startDateMaybe,
    ...endDateMaybe,
    ...startTimeMaybe,
    ...endTimeMaybe,
  };

  const currentDate = new Date();
  const expirationTime = currentDate.getTime() + 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const storageData = {
    dates: storableDates,
    selectedPackage,
    expiresAt: expirationTime,
  };

  localStorage.setItem(selectedPackage, JSON.stringify(storageData));
}

export function getRentalDateFromLocalStorage(selectedPackage) {
  const storedData = localStorage.getItem(selectedPackage);
  if (storedData) {
    const { dates, selectedPackage, expiresAt } = JSON.parse(storedData);
    const currentDate = new Date().getTime();

    // Check if stored date is within 24 hours
    if (currentDate < expiresAt) {
      return { dates, selectedPackage }; // Return stored date if valid
    }
  }

  // Remove expired or invalid data from localStorage
  localStorage.removeItem(selectedPackage);
  return null; // Return null if no valid date found or expired
}

export function normalizeString(input) {
  // Replace underscores and hyphens with spaces
  let normalized = input.replace(/[_-]/g, ' ');

  // Convert camelCase to normal by adding space before uppercase letters
  normalized = normalized.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Capitalize the first letter of each word
  normalized = normalized.replace(/\b\w/g, char => char.toUpperCase());

  // Return the final normalized string
  return normalized;
}



export const buildQueryString = (params) => {
  // Filter out undefined or null values and build query parts dynamically
  const queryParts = Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null)
    .map(key => {
      const value = params[key];
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    });

  // Join all the parts with '&' and prepend with '?'
  return queryParts.length > 0 ? `?${queryParts.join('&')}` : null;
}



export const getAdditionalFeesValues = ({ usageMultipleCharges, values }) => {

  // create usage multiple values
  const requiredUsageMultipleCharges = usageMultipleCharges.filter(charge => charge.whenToApply === REQUIRED_UPFRONT);
  const usageMultipleChargesKeys = usageMultipleCharges.map(charge => charge.feeType);
  const usageMultipleValuesKeys = Object.keys(values || {})
    .filter(key => usageMultipleChargesKeys.includes(key));

  // Ensure all required charges have been filled
  const filledAllRequiredCharges = requiredUsageMultipleCharges.every(
    charge => usageMultipleValuesKeys.includes(charge.feeType)
  );

  // Build usage multiple values object
  const usageMultipleValues = usageMultipleValuesKeys.reduce((acc, key) => {
    acc[key] = values[key];
    return acc;
  }, {});

  // Other optional fees
  const { otherOptionalFees } = values;

  return {
    filledAllRequiredCharges,
    usageMultipleValues,
    otherOptionalFees
  }

}

export function createBoundingBox(lat, lng, radiusInKm) {
  const kmToDeg = radiusInKm / 111;
  const center = [lng, lat];

  return [
    center[1] - kmToDeg, center[0] - kmToDeg, //southwest
    center[1] + kmToDeg, center[0] + kmToDeg, //northeast
  ];
}


/**
 * Sort packages in a specific order.
 *
 * The order is determined as follows:
 * 1. The default package (determined by the `defaultPackageKey` parameter)
 * 2. Per minute price
 * 3. Per hour price
 * 4. Per day price
 * 5. Overnight price
 * 6. Two days price
 * 7. One week price
 * 8. Twenty eight days price
 *
 * If a package is not present in the `packages` object, it is not included in the output.
 *
 * @param {Object} packages The packages object to sort.
 * @param {String} defaultPackageKey The key of the default package in the `packages` object.
 *
 * @returns {Object} The sorted packages object.
 */
export const sortPackages = (packages, defaultPackageKey) => {
  // Create a copy of the packages object
  const packagesCopy = { ...packages };

  // Extract the default package
  const defaultPackage = packagesCopy[defaultPackageKey];

  // Delete the default package from the copied object
  delete packagesCopy[defaultPackageKey];

  // Return the sorted packages
  return {
    ...(defaultPackage ? { [defaultPackageKey]: defaultPackage } : {}),
    ...(packagesCopy.perMinutePrice ? { perMinutePrice: packagesCopy.perMinutePrice } : {}),
    ...(packagesCopy.perHourPrice ? { perHourPrice: packagesCopy.perHourPrice } : {}),
    ...(packagesCopy.perDayPrice ? { perDayPrice: packagesCopy.perDayPrice } : {}),
    ...(packagesCopy.overnightPrice ? { overnightPrice: packagesCopy.overnightPrice } : {}),
    ...(packagesCopy.twoDaysPrice ? { twoDaysPrice: packagesCopy.twoDaysPrice } : {}),
    ...(packagesCopy.oneWeekPrice ? { oneWeekPrice: packagesCopy.oneWeekPrice } : {}),
    ...(packagesCopy.twentyEightDaysPrice ? { twentyEightDaysPrice: packagesCopy.twentyEightDaysPrice } : {})
  };
};

export const isPickupRequired = (accessOptions = []) => {
  return accessOptions.includes(ACCESS_OPTIONS.PICKUP);
};

export const isDeliveryOrShippingAvailable = (accessOptions = [], additionalFees = []) => {
  return accessOptions.includes(ACCESS_OPTIONS.DELIVERY) ||
    accessOptions.includes(ACCESS_OPTIONS.SHIP) ||
    additionalFees.find(fee => fee.feeType === SHIPPING_FEE) ||
    additionalFees.find(fee => fee.feeType === DELIVERY_FEE);
};

export const getUserDefaultCurrency = countryCode => {
  const userCountry = signupCountries.find(
    s => s.code === countryCode?.toUpperCase()
  );

  const supportedCurrency = isSupportedCurrency(
    supportedCurrencies,
    userCountry?.currency
  );

  return supportedCurrency ? userCountry.currency : null;
};

const PER_DAY = 'perDayPrice';
const PER_MINUTE = 'perMinutePrice';
const PER_HOUR = 'perHourPrice';
const TWO_DAYS = 'twoDaysPrice';
const WEEKLY_PRICE = 'oneWeekPrice';
const OVERNIGHT_PRICE = 'overnightPrice';
const TWENTY_EIGHT_DAYS = 'twentyEightDaysPrice';

export const getRentalPackage = (params) => {
  const { bookingStart, bookingEnd, selectedPackage, pricePackages } = params;

  const totalDays = moment(bookingEnd).diff(moment(bookingStart), 'days');

  if (totalDays >= 28 && pricePackages[TWENTY_EIGHT_DAYS]) {
    return TWENTY_EIGHT_DAYS;
  }

  if (totalDays >= 7 && pricePackages[WEEKLY_PRICE]) {
    return WEEKLY_PRICE;
  }

  if (totalDays >= 2 && pricePackages[TWO_DAYS]) {
    return TWO_DAYS;
  }

  if (totalDays >= 1 && selectedPackage === OVERNIGHT_PRICE && pricePackages[OVERNIGHT_PRICE]) {
    return OVERNIGHT_PRICE;
  }

  if (totalDays >= 1 && pricePackages[PER_DAY]) {
    return PER_DAY;
  }

  return selectedPackage; // Or handle other cases if needed
};

export const isWithinNinetyDays = ({ timeZone, bookingEnd }) => {

  if (!bookingEnd) {
    return false; // Return false if dates are missing
  }

  // Get current time in the given time zone (or default to UTC/local)
  const now = timeZone ? moment().tz(timeZone) : moment();
  const days = 88; //Subtract the 2 days buffer
  const ninetyDaysFromNow = now.clone().add(days, 'days');

  // Parse end dates with the same time zone
  const endDate = timeZone ? moment.tz(bookingEnd, timeZone) : moment(bookingEnd);

  // Ensure both end dates are within the future 90-day range
  return (
    endDate.isBefore(ninetyDaysFromNow) // End must be within 90 days
  );
};

let watchId = null;

const handleGeolocationError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      const permissionDeniedMessage = "User denied Geolocation.";
      console.log(permissionDeniedMessage);
      return permissionDeniedMessage;
    case error.POSITION_UNAVAILABLE:
      const positionUnavailableMessage = "Current location unavailable.";
      console.log(positionUnavailableMessage);
      return positionUnavailableMessage;
    case error.TIMEOUT:
      const timeoutMessage = "Location request timed out.";
      console.log(timeoutMessage);
      return timeoutMessage;
    case error.UNKNOWN_ERROR:
      const unknownErrorMessage = "Location error.";
      console.log(unknownErrorMessage);
      return unknownErrorMessage;
    default:
      return null;
  }
};

export const getCurrentLocationCoords = () => {
  return new Promise((resolve, reject) => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.log("Geolocation is not supported by this browser.");
      reject("Geolocation is not supported by this browser.");
      return;
    }

    const tryGetLocation = () => {
      // First try getCurrentPosition
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Clear any existing watch if it exists
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
            watchId = null;
          }
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          });
        },
        (error) => {
          console.log('getCurrentPosition error:', error);
          const errorMessage = handleGeolocationError(error);
          reject(errorMessage);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 30000
        }
      );
    };

    tryGetLocation();
  });
};

// Add a cleanup function to clear the watch
export const clearLocationWatch = () => {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
};

export const create50kmRadiusBox = (bbox, center) => {
  const [swLng, swLat, neLng, neLat] = bbox || [];

  const kmToDegLat = 50 / 111; // Latitude degree conversion
  const kmToDegLng = 50 / (111 * Math.cos((swLat + neLat) / 2 * Math.PI / 180)); // Longitude degree conversion

  // Expand the bbox by 50km
  const expandedBounds = bbox && bbox.length === 4
    ? [
      swLng - kmToDegLng, // Expanded swLng
      swLat - kmToDegLat, // Expanded swLat
      neLng + kmToDegLng, // Expanded neLng
      neLat + kmToDegLat  // Expanded neLat
    ]
    : null;

  // Construct bounds around center if bbox is missing
  const constructedBounds =
    center && center.length === 2
      ? [
        center[0] - kmToDegLng, // swLng
        center[1] - kmToDegLat, // swLat
        center[0] + kmToDegLng, // neLng
        center[1] + kmToDegLat  // neLat
      ]
      : null;

  let selectedBounds =
    expandedBounds
      ? {
        southWest: { lng: expandedBounds[0], lat: expandedBounds[1] },
        northEast: { lng: expandedBounds[2], lat: expandedBounds[3] },
      }
      : constructedBounds
        ? {
          southWest: { lng: constructedBounds[0], lat: constructedBounds[1] },
          northEast: { lng: constructedBounds[2], lat: constructedBounds[3] },
        }
        : null;

  const { southWest, northEast } = selectedBounds;
  const swLatLng = new LatLng(southWest.lat, southWest.lng);
  const neLatLng = new LatLng(northEast.lat, northEast.lng);
  const latlngBounds = new LatLngBounds(neLatLng, swLatLng);

  return latlngBounds;
};


export const getCurrentUserLocationInfo = async () => {
  try {
    const cords = await getCurrentLocationCoords();
    if (!cords || !cords.latitude || !cords.longitude) {
      throw new Error('User denied geolocation');
    }

    const locationResponse = await getLocationWithMapBox(cords.longitude, cords.latitude);
    const features = locationResponse?.features || [];

    if (!Array.isArray(features) || features.length === 0) {
      throw new Error('No features found in location response');
    }

    const getPriorityFeature = (features) => {
      return (
        features.find(f => f.id.startsWith('poi')) ||
        features.find(f => f.id.startsWith('address')) ||
        features.find(f => f.id.startsWith('locality')) ||
        null
      );
    };

    const extractLocationContext = (context = []) => {
      return context.reduce((acc, item) => {
        const type = item.id.split('.')[0];
        if (["country", "place", "district", "postcode", "region"].includes(type)) {
          acc[type] = item.text;
        }
        return acc;
      }, {});
    };

    const getBBox = (features) => {
      // first find bbox in poi, then locality, place, district, region, country
      const poi = features.find(f => f.id.startsWith('poi'));
      const locality = features.find(f => f.id.startsWith('locality'));
      const place = features.find(f => f.id.startsWith('place'));
      const district = features.find(f => f.id.startsWith('district'));
      const region = features.find(f => f.id.startsWith('region'));
      const country = features.find(f => f.id.startsWith('country'));
      const bbox = poi?.bbox || locality?.bbox || place?.bbox || district?.bbox || region?.bbox || country?.bbox || null;
      return bbox;
    };

    const feature = getPriorityFeature(features);
    if (!feature) {
      throw new Error('No relevant location feature found');
    }

    const { place_name, context } = feature;
    const { country, place, district, postcode, region } = extractLocationContext(context);
    const bbox = getBBox(features);
    const radius50kmBBox = create50kmRadiusBox(bbox, [cords.longitude, cords.latitude]);

    const userCountryObj = signupCountries.find(s => s.country === country);
    const userCurrency =
      userCountryObj && isSupportedCurrency(supportedCurrencies, userCountryObj.currency)
        ? userCountryObj.currency
        : DEFAULT_CURRENCY;

    return {
      ...(place || district || region ? { city: place || district || region } : {}),
      ...(place_name ? { address: place_name } : {}),
      ...(country ? { country: userCountryObj?.code } : {}),
      ...(postcode ? { postcode } : {}),
      origin: {
        lat: cords.latitude,
        lng: cords.longitude,
      },
      bounds: radius50kmBBox,
      currency: userCurrency,
    };
  } catch (error) {
    console.log('Error in getCurrentUserLocationInfo:', error);
    return null;
  }
};


export const extractUserCurrencyAndLocation = (currentUser) => {
  if (!currentUser?.id) return null;

  const { profile } = currentUser.attributes;
  const { publicData } = profile;
  const {
    address,
    city,
    country: countryCode,
    origin,
    postcode,
  } = publicData;
  let userCurrency = publicData.currency;

  const userCountry = signupCountries.find(s => s.code === countryCode);
  const userCurrencySupported = isSupportedCurrency(supportedCurrencies, userCountry?.currency);

  if (!userCurrency && !!userCountry && userCurrencySupported) {
    userCurrency = userCountry.currency
  };

  let userLocationInfo = {
    ...(address ? { address } : {}),
    ...(city ? { city } : {}),
    ...(countryCode ? { country: countryCode } : {}),
    ...(origin ? { origin } : {}),
    ...(postcode ? { postcode } : {}),
  };

  return {
    currency: userCurrency,
    locationInfo: userLocationInfo,
  };
}


export const isEmptyReview = (review) => review === EMPTY_REVIEW;

const handleLocalStorageError = (err) => {
  if (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  ) {
    console.error('Please reduce your data size or signup for saving your changes.', err);
    return new Error('Please reduce your data size or signup for saving your changes');
  } else {
    console.error('Failed to save your changes.', err);
    return new Error('Failed to save your changes');
  }
};

export const generateLocalListingId = () => {
  return new UUID(`local_${Math.random().toString(36).substring(2, 10)}`);
}

const TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days 

export const saveListingDraft = (listingId, tabData) => {
  try {
    console.log('saveListingDraft', listingId, tabData);

    if (typeof listingId !== 'object' || !listingId.uuid) {
      throw new Error('Invalid listingId');
    }

    if (!tabData || typeof tabData !== 'object') {
      throw new Error('Invalid tabData');
    }

    const key = `listingDraft_${listingId.uuid}`;
    const now = Date.now();

    // Try retrieving existing data
    let existing = {};
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        existing = JSON.parse(stored);
      }
    } catch (readErr) {
      console.warn('Failed to read existing draft from localStorage', readErr);
      throw new Error('Failed to read existing draft from localStorage');
    }

    const finalData = {
      id: listingId,
      type: 'listing',
      attributes: {
        title: tabData.title ?? existing?.attributes?.title,
        description: tabData.description ?? existing?.attributes?.description,
        geolocation: tabData.geolocation ?? existing?.attributes?.geolocation,
        price: tabData.price ?? existing?.attributes?.price,
        availabilityPlan: tabData.availabilityPlan ?? existing?.attributes?.availabilityPlan,
        publicData: {
          ...(existing?.attributes?.publicData || {}),
          ...(tabData.publicData || {}),
        },
        privateData: {
          ...(existing?.attributes?.privateData || {}),
          ...(tabData.privateData || {}),
        },
        createdAt: existing?.attributes?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        state: LISTING_STATE_DRAFT,
      },
      _meta: {
        savedAt: now,
        expiry: now + TTL_MS,
      },
    };

    console.log('finalData', finalData);

    try {
      localStorage.setItem(key, JSON.stringify(finalData));
      localStorage.setItem('localListingId', JSON.stringify(listingId));
    } catch (writeErr) {
      throw handleLocalStorageError(writeErr);
    }
  } catch (e) {
    const message = e.message || 'Failed to save listing draft';
    throw new Error(message);
  }
};



export const getListingDraft = (listingId) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const key = `listingDraft_${listingId}`
  const draft = JSON.parse(localStorage.getItem(key)) || {}
  return draft;
}

export const deleteListingDraft = (listingId) => {
  const key = `listingDraft_${listingId}`
  localStorage.removeItem(key);
  localStorage.removeItem('localListingId');
}

export const saveAvailabilityExceptions = (listingId, availabilityExceptions) => {
  const key = `${listingId}_availabilityExceptions`
  try {
    localStorage.setItem(key, JSON.stringify(availabilityExceptions))
  } catch (writeErr) {
    throw handleLocalStorageError(writeErr);
  }
}

export const getAvailabilityExceptions = (listingId) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const key = `${listingId}_availabilityExceptions`
  return JSON.parse(localStorage.getItem(key)) || {}
}

export const deleteAvailabilityExceptions = (listingId) => {
  const key = `${listingId}_availabilityExceptions`
  localStorage.removeItem(key)
}

export const dataURLtoFile = (dataurl, filename) => {
  // 1. Split the data URL into metadata and base64 parts
  const arr = dataurl.split(',');

  // 2. Extract the MIME type from the metadata
  const mime = arr[0].match(/:(.*?);/)[1];

  // 3. Decode the base64 string to binary
  const bstr = atob(arr[1]);

  // 4. Create a Uint8Array to hold the binary data
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  // 5. Fill the array with character codes
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // 6. Create and return a File object
  return new File([u8arr], filename, { type: mime });
}

/**
 * Save a listing image locally
 * @param {string} listingId - The ID of the listing
 * @param {File} file - The file to save
 * @param {string} imageId - The ID of the image
 * @returns {Promise<Array>} - A promise that resolves to an array of images
 */
export const saveListingImageLocally = (listingId, file, imageId) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const key = `${listingId}_images`;
      let images = {};

      try {
        const stored = localStorage.getItem(key);
        images = stored ? JSON.parse(stored) : {};
        images[imageId] = {
          imageId,
          dataUrl: reader.result, // base64 string
          filename: file.name,
        };

        localStorage.setItem(key, JSON.stringify(images));

        // Only resolve if everything above succeeds
        resolve(
          Object.values(images).map(image => ({
            file: dataURLtoFile(image.dataUrl, image.filename),
            imageId: image.imageId,
          }))
        );
      } catch (err) {
        reject(handleLocalStorageError(err));
      }
    };

    reader.onerror = (err) => {
      reject(err);
    };

    reader.readAsDataURL(file);
  });
};


/**
 * Get listing images locally
 * @param {string} listingId - The ID of the listing
 * @returns {Promise<Array>} - A promise that resolves to an array of images
 */
export const getListingImages = (listingId) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const key = `${listingId}_images`;
  const images = JSON.parse(localStorage.getItem(key)) || {};
  return Object.values(images).map((image) => {
    return {
      file: dataURLtoFile(image.dataUrl, image.filename),
      imageId: image.imageId,
    };
  });
}

export const deleteListingImage = (listingId, imageId) => {
  const key = `${listingId}_images`;
  const images = JSON.parse(localStorage.getItem(key)) || {};
  delete images[imageId];
  localStorage.setItem(key, JSON.stringify(images));
}

export const deleteListingImages = (listingId) => {
  const key = `${listingId}_images`;
  localStorage.removeItem(key);
}

export const isEditListingPage = (pathname) => {
  const path = pathname.split('/');
  return path[1] === 'listing' &&
    ['edit', 'new', 'draft'].includes(path[4]) &&
    ['details', 'location', 'availability', 'pricing', 'photos'].includes(path[5]);
};

export const hasExistingDraftListing = () => {
  const localListingIdStr = typeof window !== 'undefined' && window.localStorage.getItem('localListingId');
  if (!localListingIdStr) {
    return false;
  }

  try {
    const localListingId = JSON.parse(localListingIdStr);
    const key = `listingDraft_${localListingId.uuid}`;
    const draft = localStorage.getItem(key);
    return !!draft;
  } catch (e) {
    console.error('Error checking for existing draft listing:', e);
    return false;
  }
};


/**
 * Extract location components from an address string
 * @param {string} address - The address string to extract components from
 * @returns {string} - The extracted location component
 */
export const extractLocationComponents = (address = '') => {
  if (!address) return null;

  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  const len = parts.length;

  const components = {
    city: parts[len - 3] || null,
    area: parts[len - 4] || null,
    place: parts[len - 5] || null,
    county: parts[len - 2] || null,
    district: parts[len - 2] || null,
    country: parts[len - 1] || null,
  };

  const value = Object.values(components).find(Boolean);
  return value;
}

export const getLocationText = (city, postcode, region) => {
  // Trim inputs if they exist, to handle cases like whitespace-only strings
  city = city?.trim();
  postcode = postcode?.trim();
  region = region?.trim();

  // Check if all three are empty
  if (!city && !postcode && !region) {
    return null;
  }

  // Build the message based on available inputs
  let message = ``;

  if (city) {
    message += ` in ${city}`;
  }

  if (region) {
    if (city) {
      message += `, ${region}`;
    } else {
      message += ` in ${region}`;
    }
  }

  if (postcode) {
    if (city || region) {
      message += `, ${postcode}`;
    } else {
      message += ` in ${postcode}`;
    }
  }

  message += '.';

  return message;
};


export const normalizeDeliveryOptions = (textArray) => {
  if (!textArray || textArray.length === 0) return [];
  return textArray.map(text => {
    try {
      // Specific replacement for "pickupat" to "pickup at"
      let normalizedText = text.replace(/pickupat/gi, 'pickup at');

      // Convert camelCase to spaces (in case of camelCase strings)
      const camelCaseToSpaces = normalizedText.replace(/([a-z])([A-Z])/g, '$1 $2');

      // Replace underscores with spaces and convert to lowercase
      const wordsWithSpaces = camelCaseToSpaces.replace(/_/g, ' ').toLowerCase();

      return wordsWithSpaces;

    } catch (error) {
      console.log('Error normalizing delivery option:', error);
      return text;
    }
  });
}

/**
 * Reorders an array of objects based on a specified order of keys
 * @param {Array} array - The array of objects to reorder
 * @param {Array} keyOrder - Array of keys in the desired order
 * @returns {Array} - The reordered array
 */
export const reorderArray = (array, keyOrder) => {
  // Create a map for O(1) lookup of items by key
  const itemMap = new Map(array.map(item => [item.key, item]));

  // Create the reordered array based on the keyOrder
  const reorderedArray = keyOrder
    .map(key => itemMap.get(key))
    .filter(item => item !== undefined); // Remove any keys that don't exist

  // Add any remaining items that weren't in the keyOrder
  const remainingItems = array.filter(item => !keyOrder.includes(item.key));

  return [...reorderedArray, ...remainingItems];
}

// Helper function to fetch an image from a URL as a Blob
export const fetchImageAsBlob = async imageUrl => {
  const response = await fetch(imageUrl);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch image: ${response.status} ${errorText}`);
  }
  return response.blob();
};

// Function to upload an image to Sharetribe using a remote URL
export const uploadImageToSharetribe = async (sdk, imageUrl, filename = 'uploaded-image.jpg') => {
  if (!sdk || typeof sdk.images?.upload !== 'function') {
    throw new Error('Sharetribe SDK instance with images.upload method is required.');
  }
  if (!imageUrl) {
    throw new Error('Image URL is required.');
  }

  try {
    // const imageBlob = await fetchImageAsBlob(imageUrl);

    // Sharetribe SDK's images.upload expects a File object.
    // We create a File object from the Blob.
    // const file = new File([imageBlob], filename, { type: imageBlob.type });

    // Upload using the Sharetribe SDK
    // The exact parameters for sdk.images.upload might vary based on your SDK version or setup.
    // This example assumes it takes an object with a 'file' property.
    const response = await sdk.images.upload({ image: imageUrl }); // or { file: file } depending on SDK

    // Check if the upload was successful and return the relevant data
    // The structure of 'response.data' will depend on the Sharetribe SDK
    if (response && response.data) {
      return response.data;
    } else {
      // Handle cases where the response might not be as expected
      throw new Error('Image upload to Sharetribe did not return expected data.');
    }
  } catch (error) {
    console.error('Error uploading image to Sharetribe:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
};

export const base64ToFile = (base64String, filename) => {
  const arr = base64String.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}


export const isPublishButtonDisabled = (listingData) => {
  if (!listingData) return true;

  // Check required top-level fields
  const requiredTopLevelFields = ['title', 'description', 'publicData', 'price', 'geolocation', 'availabilityPlan'];
  for (const field of requiredTopLevelFields) {
    if (!listingData[field]) return true;
  }

  // Check required publicData fields
  const { publicData } = listingData;
  const requiredPublicDataFields = [
    'category',
    'Condition_of_the_item',
    'replacement_value',
    'location',
    'prices',
    'defaultPriceKey',
  ];

  for (const field of requiredPublicDataFields) {
    if (!publicData[field]) return true;
  }

  // Check location has address
  if (!publicData.location?.address) return true;

  // Check price is valid
  if (!listingData.price?.amount || !listingData.price?.currency) return true;

  // Check geolocation coordinates
  if (!listingData.geolocation || isNaN(listingData.geolocation.lat) || isNaN(listingData.geolocation.lng)) return true;

  // Check at least one image exists
  if (!publicData.listingImages || publicData.listingImages.length === 0) return true;

  // All checks passed - button should be enabled
  return false;
}; 

export const isSaveAsDraftButtonDisabled = (listingData) => {
  if (!listingData || Object.keys(listingData || {}).length <= 0) return true;

  // Check required fields to save as draft
  const requiredTopLevelFields = ['title', 'description'];
  for (const field of requiredTopLevelFields) {
    if (!listingData[field]) return true;
  };

  return false;
};

export const getEnhancedContent = (field, enhancedContent, rating) => {
  if (!field || !enhancedContent) return null;

  if (field === 'title') return enhancedContent;
  if (field === 'description') return enhancedContent?.content;
  if (field === 'instructions') return { text: enhancedContent?.content };
  if (field === 'where_the_item_can_be_used') return enhancedContent?.content;
  if (field === 'online_review_summary') return { content: enhancedContent?.content, rating };

  return enhancedContent[field];
}
    
  


export const checkPremiumMember = (currentUser) => {
  return currentUser?.attributes?.profile?.publicData?.premiumMember || false;
};

export const urlToFile = async (url, filename, mimeType ) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
}

export const removeNestedEmptyTags = (htmlString) => {
  if(typeof htmlString !== 'string' || typeof window === 'undefined') return htmlString;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const isNodeEmpty = (node) => {
    // Text content with only whitespace or &nbsp;
    const text = node.textContent.replace(/\u00a0/g, '').trim();
    return (
      !text &&
      !node.querySelector('*:not(:empty)') // no non-empty child elements
    );
  };

  const cleanEmptyElements = (element) => {
    const children = Array.from(element.children);
    for (const child of children) {
      cleanEmptyElements(child);
      if (isNodeEmpty(child)) {
        child.remove();
      }
    }
  };

  cleanEmptyElements(doc.body);
  return doc.body.innerHTML.trim();
};

export const sanitizeHtml = html => {
  const sanitizedHtml = html.replace(/＜/g, '<').replace(/＞/g, '>');
  return removeNestedEmptyTags(sanitizedHtml);
};


export const getCustomPackagePrice = (days, pricePackages) => {
  // Map package keys to their durations
  const packageDurations = {
      twoDaysPrice: 2,
      perDayPrice: 1,
      oneWeekPrice: 7,
      twentyEightDaysPrice: 28,
  };

  

  // Filter available and daily packages and sort by duration
  const availablePackages = Object.entries(pricePackages)
      .filter(([key, pkg]) => pkg && pkg.amount && packageDurations[key])
      .map(([key, pkg]) => ({
          key,
          duration: packageDurations[key],
          amount: pkg.amount,
          currency: pkg.currency,
      }))
      .sort((a, b) => a.duration - b.duration);

  if (availablePackages.length === 0) return null;

  // Find the best package for the given days
  let bestPackage = availablePackages[0];
  for (let i = 0; i < availablePackages.length; i++) {
      if (availablePackages[i].duration <= days) {
          bestPackage = availablePackages[i];
      }
  }
  // Calculate price
  const quantity = days / bestPackage.duration;
 

  const amount = new Decimal(bestPackage.amount);
  let totalAmount = amount.mul(quantity).toNumber();

  // Find the next bigger package (if any)
  // Find the next best (longer duration but cheaper) package
  const findNextMinimumPrice = (days, totalAmount, packages) => {
      return packages
          .filter(pkg => pkg.duration > days && pkg.amount < totalAmount)
          .reduce((cheapest, pkg) => {
              if (!cheapest || pkg.amount < cheapest.amount) return pkg;
              return cheapest;
          }, null);
  };

  const nextPackage = findNextMinimumPrice(days, totalAmount, availablePackages);
  if (nextPackage) {
      totalAmount = nextPackage.amount;
  }

  // Find the minimum price package, if the total amount is less than 
  // minimum price package, cap the total amount to the minimum price package
  const minimumPricePackage = availablePackages.reduce((min, pkg) => {
    if (!min || pkg.amount < min.amount) return pkg;
    return min;
  }, null);

  if (totalAmount < minimumPricePackage.amount) {
    totalAmount = minimumPricePackage.amount;
  };

  return new Money(totalAmount, bestPackage.currency);
};

/**
 * Calculate the distance between two geographic coordinates using the Haversine formula
 * @param {number} lat1 - Latitude of the first point in degrees
 * @param {number} lon1 - Longitude of the first point in degrees
 * @param {number} lat2 - Latitude of the second point in degrees
 * @param {number} lon2 - Longitude of the second point in degrees
 * @returns {Object} - Object containing distance in miles and kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  // Earth's radius in kilometers
  const R = 6371;

  // Convert degrees to radians
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;

  // Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceKm = R * c;

  // Convert to miles (1 km = 0.621371 miles)
  const distanceMiles = distanceKm * 0.621371;

  return {
    miles: Math.round(distanceMiles * 10) / 10, // Round to 1 decimal place
    kilometers: Math.round(distanceKm * 10) / 10 // Round to 1 decimal place
  };
}

export const isHTMLString = str =>
  typeof str === 'string' &&
  /<\/?[a-zA-Z][\w:-]*(\s+[^<>]*)?>/.test(str);


// Basic example using DOMParser or regex (depending on environment)
export const stripHtmlTags = (richTextHtml, maxLength = 160) => {
  if (!richTextHtml || typeof richTextHtml !== 'string' || typeof window === 'undefined') return '';

  // Normalize full-width brackets (e.g., ＜ol＞ to <ol>)
  const normalizedHtml = sanitizeHtml(richTextHtml);

  // Use DOMParser to safely extract plain text
  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizedHtml, 'text/html');
  const plainText = doc.body.textContent || '';

  // Normalize whitespace and trim
  const cleanedText = plainText.replace(/\s+/g, ' ').trim();

  // Truncate for meta
  return cleanedText.length > maxLength
    ? cleanedText.slice(0, maxLength - 3).trim() + '...'
    : cleanedText;

};

export const getAllowedBlogCount = (replacementValue) => {
  const amount = replacementValue?.max?.amount;

  if (typeof amount !== 'number') return 0;

  if (amount > 50000) {
    return 5;
  } else {
    return 2;
  }
}
